import { Injectable }                from '@angular/core';
import { Http, Response,
         URLSearchParams }           from '@angular/http';
// Services
import { ApiService }                from '../root-services/api.service';
import { EditionListService }        from '../editions/edition.service';
// Interfaces
import { AnalyticsJSONImport }       from './../models/analytics/analytics-json-import';
import { RankingsData }              from '../models/analytics/analytics-rankings';
import { AnalyticsDataset }          from '../models/analytics/analytics-dataset';
import { AnalyticsStateObject }      from '../models/analytics/analytics-state-object';
import { EditionDetails }            from '../models/global/edition-details';
import { ResProgFilter }             from '../models/analytics/resProgFilter';
// RxJs Interfaces
import { Observable }                from 'rxjs/Observable';
import { DropdownFilterOptions }     from '../models/analytics/dropdown-filter-options';
import { AnalyticsSubscriptionData } from '../models/analytics/analytics-subscription-user';
// Utilities
import { addQueryParam }             from './utils';


@Injectable()
export class AnalyticsService {
  private analyticsCacheObj;

  constructor(
    private api: ApiService,
    private editionList: EditionListService,
    private http: Http
  ) {
    editionList.waitForEdition().subscribe(() => this.clearCache());
    if (sessionStorage.analyticsCacheObj) {
        // TODO: Are there security issues with this?
        this.analyticsCacheObj = JSON.parse(sessionStorage.getItem('analyticsCacheObj'));
    } else {
        this.analyticsCacheObj = {};
    }
  }

  checkCacheGetJSON(url: string, params?: any): Observable<any> {
    let returnObservable;
    let disableCache = JSON.parse(localStorage.getItem('disableCache'));
    let editionId = localStorage.getItem('edition_id');

    if (params) {
      Object.keys(params).forEach(key => {
        url = addQueryParam(url, key, params[key]);
      });
    }

    const cacheUrl = addQueryParam(url, 'edition_id', editionId);

    if (
      disableCache ||
      !this.analyticsCacheObj ||
      !this.analyticsCacheObj[cacheUrl] ||
      this.analyticsCacheObj[cacheUrl].timeStamp - Date.now() >
        1000 * 60 * 60
    ) {
      returnObservable = this.api
        .get(url)
        .map((response: Response) => response.json())
        .map(json => {
          this.cacheJSON(cacheUrl, json);
          return json;
        });
    } else {
      returnObservable = Observable.of(
        this.analyticsCacheObj[cacheUrl].data
      );
    }
    return returnObservable;
  }

  cacheJSON(url: string, json: {}) {
    this.analyticsCacheObj[url] = {
        timeStamp: Date.now(),
        data: json
    }
    if (sessionStorage) {
        sessionStorage.setItem('analyticsCacheObj', JSON.stringify(this.analyticsCacheObj));
    }
  }

  clearCache(): void {
      if (sessionStorage) {
          sessionStorage.removeItem('analyticsCacheObj');
      }
      this.analyticsCacheObj = {};
  }

  getGraphJSON(obj: {
      stateObj: any,
      numberOfDays: number,
      liveFilterRef: string,
      resProgFilter?: ResProgFilter,
      perUser?: boolean,
      breakdownChoice?: string,
      urlOverride?: string
    }): Observable<any> {
    let returnObservable: Observable<any>;
    let edition = this.editionList.getEdition();

    let url = obj.urlOverride;
    if (!url) {
      if (obj.stateObj.subscriptionId) {
        url = `/analytics/subscriptions/${obj.stateObj.subscriptionId}/${obj.stateObj.graphType}`;
      } else {
        url = '/analytics/' + obj.stateObj.graphType;
      }
    }

    let params = new URLSearchParams();
    params.set('days', obj.numberOfDays.toString());
    params.set('state', obj.liveFilterRef);

    if (
      obj.resProgFilter &&
      obj.resProgFilter === 'resProgComm' ||
      obj.resProgFilter === 'resProg' ||
      obj.resProgFilter === 'resources' ||
      obj.resProgFilter === 'programs' ||
      obj.resProgFilter === 'community'
    ) {
      params.set('resProg', obj.resProgFilter);
    }

    if (obj.stateObj.categoryType === 'edition') {
      params.set('edition_id', edition.id.toString());
    } else {
      params.set(obj.stateObj.categoryType + '_id', obj.stateObj.categoryId);
    }

    if (obj.breakdownChoice) {
      params.set('breakdown', obj.breakdownChoice);
    }

    if (obj.perUser) {
      params.set('per_user', 'true');
    }

    let querystring = params.toString();
    url += '?' + querystring;

    return this.checkCacheGetJSON(url)
      .map((JSONdataset) => {
        if (obj.breakdownChoice) {
          return JSONdataset.map(data => this.reformatData(data, obj.breakdownChoice));
        } else {
          return this.reformatData(JSONdataset, obj.stateObj.categoryType);
        }
      })
      .publishReplay(1).refCount()
      .catch(this.handleError);
  }

  reformatData(dataObj, categoryType) {
    let dataset: AnalyticsDataset;
    dataset = {
      barChartData: [
        {data: [], unit: dataObj.unit}
      ],
      label: dataObj.title,
      barChartLabels: [],
      id: dataObj.id,
      categoryType: categoryType,
      availableBreakdowns: dataObj.available_breakdowns,
      hasData: dataObj.has_data
    };
    if(dataObj.total) {
      dataset.total = dataObj.total;
    }
    if(dataObj.is_fm) {
      dataset.is_fm = dataObj.is_fm;
    }
    dataObj.data.forEach((value) => {
      dataset.barChartData[0].data.push(parseInt(value[1], 10));
      dataset.barChartLabels.push(value[0]);
    });

    if(dataObj.secondary_unit) {
      let secondaryData = [];
      dataObj.data.forEach((value) => {
        secondaryData.push(parseInt(value[2], 10));
      });
      dataset.barChartData.push({data: secondaryData, unit: dataObj.secondary_unit});

      if(dataObj.secondary_total) {
        dataset.secondaryTotal = dataObj.secondary_total;
      }
    }
    return dataset;
  }

  getProgramList(): Observable<any> {
    return this.api.get('/programs')
      .map((response: Response) => {
        return response.json()
          .map(obj => {
            obj.type = 'program';
            return obj;
          });
      })
      .publishReplay(1).refCount()
      .catch(this.handleError);
  }

  getWavesEtcList(programId: number): Observable<any> {
    let baseUrl = '/programs' + '/' + programId;
    return Observable.forkJoin([
        this.api.get(baseUrl + '/waves')
          .map((res: Response) => {
            return res.json().map(obj => {
              obj.programId = programId;
              obj.type = 'wave';
              return obj;
            });
          }),
        this.api.get(baseUrl + '/lone_groups')
          .map((res: Response) => {
            return res.json().map(obj => {
              // TODO: Is group the same as lone_group for get group API?
              obj.programId = programId;
              obj.type = 'group';
              return obj;
            });
          }),
      ]).map(results => {
          return results[0].concat(results[1]);
      })
      .publishReplay(1).refCount()
      .catch(this.handleError);
  }

  getGroupList(programId: number, waveId: number): Observable<any> {
    return this.api.get('/programs/' + programId + '/waves/' + waveId + '/groups')
      .map((response: Response) => {
        return response.json().map(obj => {
          obj.type = 'group';
          obj.programId = programId;
          return obj;
        });
      })
      .publishReplay(1).refCount()
      .catch(this.handleError);
  }

  getEditionDetails(): Observable<Response> {
    return this.api.get('/edition');
  }

  getProgram(programId: number) {
    return this.api.get('/programs/' + programId);
  }

  getJourney(journeyId: number) {
    return this.api.get('/journeys/' + journeyId);
  }

  getResource(resourceId: number) {
    return this.api.get('/resources/' + resourceId);
  }

  getWave(programId: number, waveId: number) {
    console.log('/programs/' + programId + '/waves/' + waveId);
    return this.api.get('/programs/' + programId + '/waves/' + waveId);
  }

  getGroup(programId: number, groupId: number) {
    return this.api.get('/programs/' + programId + '/groups/' + groupId);
  }

  getSubscriptionUsers(stateObj: AnalyticsStateObject, subscriptionId: number): Observable<AnalyticsSubscriptionData> {
    let url = `/analytics/subscriptions/${subscriptionId}/users`;
    let params = new URLSearchParams();

    if (stateObj.categoryType !== 'edition') {
      params.set(stateObj.categoryType + '_id', stateObj.categoryId.toString());
    }

    let querystring = params.toString();
    url += '?' + querystring;

    return this.checkCacheGetJSON(url)
        .map((json: {}) => json as AnalyticsSubscriptionData);
  }

  getUserRankings(obj, days?: number): Observable<RankingsData> {
    let url = '/analytics/user_rankings';
    let params = new URLSearchParams();
    if (obj.categoryType !== 'edition') {
      params.set(obj.categoryType + '_id', obj.categoryId);
    }
    if (days !== undefined) {
      params.set('days', days.toString());
    }
    let querystring = params.toString();
    if (querystring.length) {
      url += '?' + querystring;
    }

    return this.checkCacheGetJSON(url)
        .map((json: {}) => json as RankingsData);
  }

  getTeamRankings(obj, days?: number): Observable<RankingsData> {
    let url = '/analytics/team_rankings';
    let params = new URLSearchParams();
    if (obj.categoryType !== 'edition') {
      params.set(obj.categoryType + '_id', obj.categoryId);
    }
    if (days !== undefined) {
      params.set('days', days.toString());
    }
    let querystring = params.toString();
    if (querystring.length) {
      url += '?' + querystring;
    }

    return this.checkCacheGetJSON(url)
        .map((json: {}) => json as RankingsData);
  }

  getComments(
      obj,
      days: number,
      liveFilterRef: string,
      resProgFilter?: ResProgFilter,
      breakdown?: string
    ): Observable<any> {
      let edition = this.editionList.getEdition();
      let url = '/analytics/comments';
      if (obj.subscriptionId) {
        url = `/analytics/subscriptions/${obj.subscriptionId}/comments`;
      }
      let params = new URLSearchParams();
      if (obj.categoryType !== 'edition') {
        params.set(obj.categoryType + '_id', obj.categoryId);
      }
      else {
        params.set('edition_id', edition.id.toString());
      }
      if (days !== undefined) {
        params.set('days', days.toString());
      }
      if (liveFilterRef !== undefined) {
          params.set('state', liveFilterRef);
      }
      if (resProgFilter !== undefined) {
        params.set('resProg', resProgFilter);
      }
      if (breakdown) {
        params.set('breakdown', breakdown);
      }
      let querystring = params.toString();
      if (querystring.length) {
        url += '?' + querystring;
      }

      return this.checkCacheGetJSON(url);
  }

  getInteractions(
    obj,
    days: number,
    liveFilterRef: string,
    perUser?: boolean,
    resProgFilter?: ResProgFilter,
    breakdown?: string
  ): Observable<any> {
    let edition = this.editionList.getEdition();
    let url = '/analytics/interactions';
    if (obj.subscriptionId) {
      url = `/analytics/subscriptions/${obj.subscriptionId}/interactions`;
    }
    let params = new URLSearchParams();
    if (obj.categoryType !== 'edition') {
      params.set(obj.categoryType + '_id', obj.categoryId);
    }
    else {
      params.set('edition_id', edition.id.toString());
    }
    params.set('days', days.toString());
    if (breakdown) {
      params.set('breakdown', breakdown);
    }
    if (liveFilterRef !== undefined) {
        params.set('state', liveFilterRef);
    }
    if (resProgFilter !== undefined) {
      params.set('resProg', resProgFilter);
    }
    if (perUser) {
      params.set('per_user', 'true');
    }
    let querystring = params.toString();
    if (querystring.length) {
      url += '?' + querystring;
    }

    return this.checkCacheGetJSON(url);
  }

  public getFmPolls(stateObj: AnalyticsStateObject): Observable<any> {
    let url = '/analytics/polls';
    let fullUrl: string;
    if (stateObj.categoryType === 'edition') {
      fullUrl = url;
    } else {
      fullUrl = `${url}?${stateObj.categoryType}_id=${stateObj.categoryId}`;
    }
    return this.checkCacheGetJSON(fullUrl);
  }

  public getPoll(stateObj: AnalyticsStateObject, id: number): Observable<any> {
    let url = `/analytics/polls/${id}`;
    console.log('stateObj.categoryType:', stateObj.categoryType);
    if (stateObj.categoryType === 'program') {
      url = `${url}?program_id=${stateObj.programId}&breakdown=wave`;
    } else if (stateObj.categoryType === 'group') {
      url = `${url}?group=${stateObj.categoryId}`;
    } else {
      url = `${url}?wave=${stateObj.categoryId}`;
    }
    console.log('url:', url);
    return this.checkCacheGetJSON(url);
  }

  generateGenericReport(ref: string, stateObj: AnalyticsStateObject) {
    let url = '/analytics/downloads/' + ref;
    if (stateObj.categoryType === 'program') {
      url = `${url}?program_id=${stateObj.programId}`;
    }
    else if (stateObj.categoryType === 'group') {
      url = `${url}?group_id=${stateObj.categoryId}`;
    }
    else if (stateObj.categoryType === 'wave') {
      url = `${url}?wave_id=${stateObj.categoryId}`;
    }
    else {
      url = addQueryParam(url, 'edition_id', this.editionList.getEdition().id);
    }
    if (stateObj.subscriptionId) {
      url = addQueryParam(url, 'subscription_id', stateObj.subscriptionId);
    }
    return this.api.get(url)
        .map((response: Response) => response.json());
  }

  generateResourceReport(stateObj: AnalyticsStateObject, days: number) {
    let url = '/analytics/downloads/resource_report';
    if (stateObj.categoryType === 'program') {
      url = `${url}?program_id=${stateObj.programId}`;
    }
    else if (stateObj.categoryType === 'group') {
      url = `${url}?group_id=${stateObj.categoryId}`;
    }
    else if (stateObj.categoryType === 'wave') {
      url = `${url}?wave_id=${stateObj.categoryId}`;
    }
    else {
      url = addQueryParam(url, 'edition_id', this.editionList.getEdition().id);
    }
    url += '&days=' + days;
    if (stateObj.subscriptionId) {
      url = addQueryParam(url, 'subscription_id', stateObj.subscriptionId);
    }
    return this.api.get(url)
        .map((response: Response) => response.json());
  }

  generateLiveFilterOptions(map, stateObj: AnalyticsStateObject): any {
    let liveFilterOptions: DropdownFilterOptions
    let category, totalCount: number;
    if (stateObj.categoryType === 'program') {
      category = map.getProgram(stateObj.categoryId);
    } else if (stateObj.categoryType === 'wave') {
      category = map.getWave(stateObj.categoryId);
    } else if (stateObj.categoryType === 'edition') {
      category = map.getEdition();
      // Total count has to come from array, programs containing archived and live teams are not mutually exclusive, so can't be summed
      totalCount = category.programs.length;
    }

    if (['program', 'wave', 'edition'].indexOf(stateObj.categoryType) > -1) {
      return [
        {ref: 'live', label: 'Live Teams', count: category.live_children},
        {ref: 'archived', label: 'Archived Teams', count: category.archived_children},
        {ref: 'all', label: 'Live & Archived', count: totalCount || (category.live_children + category.archived_children)},
      ]
    }
  }

  getBreadcrumbs(obj: AnalyticsStateObject): any {
    let url = '/analytics/breadcrumbs';
    let params = new URLSearchParams();
    if (obj.categoryType === 'edition') {
      let edition = this.editionList.getEdition();
      if (edition) {
        params.set('edition', edition.id.toString());
      }
    } else {
      params.set(obj.categoryType, obj.categoryId.toString());
    }
    let querystring = params.toString();
    if (querystring.length) {
      url += '?' + querystring;
    }
    return this.api.get(url).map(response => response.json());
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/analytics/analytics.service.ts